import React from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { symbols } from './SymbolSvgs';
import { ShoppingCart, X } from 'lucide-react';

const FONTS = [
  { id: 'Bodoni', name: 'Bodoni', family: "'Bodoni Moda', serif" },
  { id: 'Medula', name: 'Medula', family: "'Medula One', cursive" },
  { id: 'Grand Hotel', name: 'Grand Hotel', family: "'Grand Hotel', cursive" },
  { id: 'Nickainley', name: 'Nickainley', family: "'Nickainley', cursive" },
  { id: 'Arimo', name: 'Arimo', family: "'Arimo', sans-serif" },
  { id: 'Satisfy', name: 'Satisfy', family: "'Satisfy', cursive" },
];

export const ConfiguratorPanel = ({
  text,
  setText,
  selectedFont,
  setSelectedFont,
  selectedSymbol,
  setSelectedSymbol,
  symbolPosition,
  setSymbolPosition,
  onAddToCart,
  isLoading = false,
  maxChars = 40,
}) => {
  const handleTextChange = (e) => {
    const value = e.target.value;
    // Limiter à 2 lignes max
    const lines = value.split('\n');
    if (lines.length > 2) {
      return;
    }
    // Limiter les caractères par ligne
    const validLines = lines.map(line => line.slice(0, maxChars));
    const newValue = validLines.join('\n');
    setText(newValue);
  };

  const handleKeyDown = (e) => {
    // Permettre Entrée seulement s'il n'y a pas déjà 2 lignes
    if (e.key === 'Enter') {
      const lines = text.split('\n');
      if (lines.length >= 2) {
        e.preventDefault();
      }
    }
  };

  const lineCount = text.split('\n').filter(l => l.length > 0).length || 0;

  return (
    <div className="flex flex-col gap-6" data-testid="configurator-panel">
      {/* Text Input Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-neutral-700">
            Texte de la gravure
          </Label>
          <span className="text-xs text-neutral-400">
            {lineCount}/2 lignes
          </span>
        </div>
        
        <Textarea
          placeholder="Entrez votre texte"
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          className="min-h-[70px] resize-none text-lg border-neutral-200 focus:border-neutral-900 focus:ring-neutral-900"
          maxLength={maxChars * 2 + 1}
          data-testid="text-input"
        />
      </div>

      {/* Font Selector */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-neutral-700">
          Style de police
        </Label>
        
        <div 
          className="grid grid-cols-3 gap-2"
          data-testid="font-selector"
        >
          {FONTS.map((font) => (
            <button
              key={font.id}
              onClick={() => setSelectedFont(font.id)}
              className={`h-12 flex items-center justify-center border rounded-lg transition-all cursor-pointer text-base ${
                selectedFont === font.id 
                  ? 'border-neutral-900 bg-neutral-900 text-white' 
                  : 'border-neutral-200 hover:border-neutral-400'
              }`}
              style={{ fontFamily: font.family }}
              aria-pressed={selectedFont === font.id}
              data-testid={`font-option-${font.id.toLowerCase().replace(' ', '-')}`}
            >
              {font.name}
            </button>
          ))}
        </div>
      </div>

      {/* Symbol Selector */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-neutral-700">
          Symbole
        </Label>
        
        <div 
          className="flex gap-2 flex-wrap"
          data-testid="symbol-selector"
        >
          {symbols.map((symbol) => {
            const IconComponent = symbol.component;
            return (
              <button
                key={symbol.id}
                onClick={() => setSelectedSymbol(symbol.id)}
                className={`w-11 h-11 flex items-center justify-center rounded-lg border transition-all cursor-pointer ${
                  selectedSymbol === symbol.id 
                    ? 'bg-neutral-900 text-white border-neutral-900' 
                    : 'border-neutral-200 text-neutral-700 hover:border-neutral-400'
                }`}
                title={symbol.name}
                aria-pressed={selectedSymbol === symbol.id}
                data-testid={`symbol-option-${symbol.id}`}
              >
                {symbol.id === 'none' ? (
                  <X size={18} />
                ) : IconComponent ? (
                  <IconComponent size={22} />
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Symbol Position */}
        {selectedSymbol !== 'none' && (
          <div className="mt-3 p-3 bg-neutral-50 rounded-lg">
            <Label className="text-xs font-medium text-neutral-500 mb-2 block">
              Position du symbole
            </Label>
            <RadioGroup 
              value={symbolPosition} 
              onValueChange={setSymbolPosition}
              className="flex gap-4"
              data-testid="symbol-position-selector"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="before" id="before" data-testid="symbol-position-before" />
                <Label htmlFor="before" className="text-sm cursor-pointer">Avant</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="after" id="after" data-testid="symbol-position-after" />
                <Label htmlFor="after" className="text-sm cursor-pointer">Après</Label>
              </div>
            </RadioGroup>
          </div>
        )}
      </div>

      {/* Add to Cart Button */}
      <Button 
        onClick={onAddToCart}
        disabled={isLoading}
        className="w-full py-5 bg-neutral-900 text-white text-sm tracking-wider uppercase font-semibold hover:bg-black transition-all mt-2 disabled:opacity-50"
        data-testid="add-to-cart-btn"
      >
        {isLoading ? (
          <>
            <span className="animate-spin mr-2">⏳</span>
            Préparation...
          </>
        ) : (
          <>
            <ShoppingCart className="w-4 h-4 mr-2" />
            Ajouter au panier
          </>
        )}
      </Button>
    </div>
  );
};

export default ConfiguratorPanel;
